/** !
 * (c) 2019 Chris Ferdinandi, MIT License, https://gomakethings.com
 * Derived from work by Brian Suda, https://24ways.org/2010/calculating-color-contrast/
 */

/**
 * Transform hexcode to RGB values in sRGB color space
 * @param {String} hexcolor Hex color value (#000 or #000000)
 * @returns {Object} Object containing the 3 values for R G and B as Numbers.
 */
function _hexToRGB(hexcolor) {
  // Remove leading '#'
  let color = hexcolor.slice(1);

  // If a three-character hexcode, make six-character
  if (color.length === 3) {
    color = color
      .split('')
      .map(value => value + value)
      .join('');
  }

  // Convert to RGB value
  const r = parseInt(color.slice(0, 2), 16);
  const g = parseInt(color.slice(2, 4), 16);
  const b = parseInt(color.slice(4, 6), 16);

  return { r, g, b };
}

function _hexFromRGB(r, g, b) {
  // TODO: Make accept Object with R G and B keys
  let hexstring = '#';
  [r, g, b].forEach(color => hexstring.push(color.toString(16)));
  return hexstring;
}

/**
 * Convert sRGB channel value to linear space
 * @param {Number} sRGB value
 * @returns {Number} gamma corrected Linear-sRGB value
 */
function _sRGBToLinear(v) {
  return v <= 0.0405 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

/**
 * Convert linear space value to sRGB
 * @param {Number} Linear-sRGB value
 * @returns {Number} sRGB value
 */
function _sRGBFromLinear(v) {
  return v <= 0.0031308 ? v * 12.92 : Math.pow(v, 1 / 2.4) * 1.055 - 0.055;
}

/**
 * Determine if color is light or dark
 * @param {String} hexcolor Hex color value (#000 or #000000)
 * @param {Number} threshold Value between 0 and 1 to determine weighting if color is 'light'. 1 = always true, 0 = always false.
 * @returns {Boolean} Whether the param color is light (thus contrast should be dark)
 */
export function isLightColor(hexcolor, threshold = 0.35) {
  const { r, g, b } = _hexToRGB(hexcolor);
  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  // Check contrast 
  return yiq >= (1 - threshold) * 255;
}

export function darken(hexcolor, factor) {
  return _hexFromRGB(
    ...Object.values(_hexToRGB(hexcolor)).map(v =>
      _sRGBFromLinear(_sRGBToLinear(v) * (1 / factor))
    )
  );
}

export function lighten(hexcolor, factor) {
  return _hexFromRGB(
    ...Object.values(_hexToRGB(hexcolor)).map(v =>
      _sRGBFromLinear(_sRGBToLinear(v) * factor)
    )
  );
}
